/**
 * Custom buy box for individual products — cart side.
 *
 * Keeps subscription lines that are on a jar plan on the jar plan for their
 * quantity: 1 → the 40-day jar plan, 2 or more → the 80-day jar plan. The two
 * plan IDs come from snippets/individual-buybox-plan.liquid (data-jar-plans),
 * the only place they are defined, and apply only to variants that have both.
 * Matched by ID, never by plan name (names are translated and editable in Loop).
 * A line already on one of those two plans moves when qty changes
 * (80 → 40 when qty drops to 1, 40 → 80 when qty rises). Any other frequency
 * and tier buy box lines (_tier) are left alone. One-time → Subscribe is chosen
 * in snippets/cart-subscription-restore-plan-id.liquid with the same 40/80 rule.
 *
 * Standalone (no webpack), loaded from layout/theme.liquid. It wraps fetch and,
 * after any successful /cart/add, /cart/change or /cart/update, moves lines that
 * are off their jar plan (/cart/change.js with selling_plan) BEFORE handing the
 * response back — with the corrected cart and sections — so the drawer or cart
 * page renders once, from the fixed cart, inside the theme's own checkout lock.
 * That covers quantity changes, Subscribe switched on in the cart (its restore
 * plan already comes from Liquid), adds from anywhere, and 1 + 1 jar merging
 * into a 2-jar line. data-check-on-load: Liquid saw an off-plan line at render
 * time (e.g. a full-page add), so it is fixed once on load.
 */
(function () {
  'use strict';

  if (window.__FROYA_IBB_CART__) return;
  window.__FROYA_IBB_CART__ = true;

  var script = document.currentScript;
  var JAR_PLANS = String((script && script.getAttribute('data-jar-plans')) || '').split(',');
  var PLAN_1 = (JAR_PLANS[0] || '').trim();
  var PLAN_2 = (JAR_PLANS[1] || '').trim();
  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var nativeFetch = window.fetch;
  var products = {};

  // The line's own URL is already localized (/de/products/<translated-handle>).
  function loadProduct(line) {
    var base = line.url ? String(line.url).split('?')[0] : line.handle ? ROOT + 'products/' + encodeURIComponent(line.handle) : '';
    if (!base) return Promise.resolve(null);
    if (!products[base]) {
      products[base] = nativeFetch
        .call(window, base + '.js', { credentials: 'same-origin' })
        .then(function (res) { return res.ok ? res.json() : null; })
        .catch(function () { return null; });
    }
    return products[base];
  }

  // 1 → 40-day plan. 2 or more → 80-day plan. Never a 120-day lookup.
  function supplyQty(qty) {
    var n = Number(qty) || 1;
    if (n < 1) return 1;
    return n > 1 ? 2 : 1;
  }

  function jarPlan(product, variantId, qty) {
    var want = qty > 1 ? PLAN_2 : PLAN_1;
    if (!want) return '';
    var variant = (product.variants || []).filter(function (v) { return String(v.id) === String(variantId); })[0];
    var allocations = (variant && variant.selling_plan_allocations) || [];
    for (var i = 0; i < allocations.length; i++) {
      if (String(allocations[i].selling_plan_id) === want) return want;
    }
    return '';
  }

  // The plan this line should move to, or '' when it is fine as it is. Only a
  // line already on one of its jar plans moves: any other plan (a frequency the
  // customer picked, a forced program plan) is theirs and is left alone.
  function targetPlan(line) {
    var current = line && line.selling_plan_allocation && line.selling_plan_allocation.selling_plan;
    if (!current || (line.properties && line.properties._tier)) return Promise.resolve('');
    // Cheap pre-check before any fetch: only a line on one of the two jar plans can move.
    var currentId = String(current.id);
    if (!currentId || (currentId !== PLAN_1 && currentId !== PLAN_2)) return Promise.resolve('');
    return loadProduct(line).then(function (product) {
      if (!product) return '';
      var id = String(current.id);
      var one = jarPlan(product, line.variant_id, 1);
      var two = jarPlan(product, line.variant_id, 2);
      if (!one || !two || (id !== one && id !== two)) return '';
      var want = jarPlan(product, line.variant_id, supplyQty(line.quantity));
      return want && want !== id ? want : '';
    });
  }

  function findFixes(lines) {
    return Promise.all((lines || []).map(function (line) {
      return targetPlan(line).then(function (plan) {
        return plan ? { key: line.key, quantity: line.quantity, plan: plan } : null;
      });
    })).then(function (list) { return list.filter(Boolean); });
  }

  // Resolves with the last /cart/change.js cart, or null if a change failed.
  function applyFixes(fixes, sections) {
    return fixes.reduce(function (chain, fix, index) {
      return chain.then(function (previous) {
        if (index > 0 && !previous) return null;
        var body = { id: fix.key, quantity: fix.quantity, selling_plan: fix.plan };
        if (sections && index === fixes.length - 1) body.sections = sections;
        return nativeFetch
          .call(window, ROOT + 'cart/change.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body)
          })
          .then(function (res) { return res.ok ? res.json() : null; });
      });
    }, Promise.resolve(true));
  }

  // --- fetch hook

  function cartMutation(input, init) {
    var method = String((init && init.method) || (input && input.method) || 'GET').toUpperCase();
    if (method !== 'POST') return '';
    var url = typeof input === 'string' ? input : (input && input.url) || String(input || '');
    var path;
    try {
      path = new URL(url, window.location.href).pathname;
    } catch (e) {
      path = url.split('?')[0];
    }
    var match = path.match(/\/cart\/(add|change|update)(\.js)?$/);
    return match ? match[1] : '';
  }

  function requestedSections(init) {
    var body = init && init.body;
    if (!body) return null;
    if (typeof body === 'string') {
      try {
        var parsed = JSON.parse(body);
        return (parsed && parsed.sections) || null;
      } catch (e) {
        return null;
      }
    }
    if (typeof FormData !== 'undefined' && body instanceof FormData) return body.get('sections') || null;
    return null;
  }

  function settle(res, kind, init) {
    if (!res || !res.ok) return res;
    return res.clone().json().then(function (data) {
      var lines = kind === 'add' ? (Array.isArray(data.items) ? data.items : [data]) : data.items;
      return findFixes(lines).then(function (fixes) {
        if (!fixes.length) return res;
        return applyFixes(fixes, requestedSections(init)).then(function (latest) {
          if (!latest) return res;
          var patched = latest;
          if (kind === 'add') {
            // An add answers with the added line(s): keep that shape, but with the
            // corrected lines (new key and plan) and fresh sections.
            var fixed = {};
            fixes.forEach(function (fix) { fixed[fix.key] = fix; });
            var swap = function (item) {
              var fix = item && fixed[item.key];
              if (!fix) return item;
              return (latest.items || []).filter(function (l) {
                return String(l.variant_id) === String(item.variant_id) &&
                  l.selling_plan_allocation && String(l.selling_plan_allocation.selling_plan.id) === fix.plan;
              })[0] || item;
            };
            if (Array.isArray(data.items)) {
              patched = data;
              patched.items = data.items.map(swap);
            } else {
              patched = Object.assign({}, swap(data));
            }
            if (latest.sections) patched.sections = latest.sections;
          }
          return new Response(JSON.stringify(patched), { status: res.status, statusText: res.statusText, headers: res.headers });
        });
      });
    }).catch(function () { return res; });
  }

  window.fetch = function (input, init) {
    var kind = '';
    try {
      kind = cartMutation(input, init);
    } catch (e) {
      kind = '';
    }
    if (!kind) return nativeFetch.apply(this, arguments);
    return nativeFetch.apply(this, arguments).then(function (res) {
      return settle(res, kind, init);
    });
  };

  // --- Page-load check (no theme request in flight, so this one re-renders itself)

  function fixOnLoad() {
    var root = document.documentElement;
    var isCartPage = !!(window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.pageType === 'cart');
    nativeFetch
      .call(window, ROOT + 'cart.js', { credentials: 'same-origin' })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (cart) {
        if (!cart) return;
        return findFixes(cart.items).then(function (fixes) {
          if (!fixes.length) return;
          root.dispatchEvent(new CustomEvent('line-item-quantity:change:start', { bubbles: true, detail: {} }));
          return applyFixes(fixes, [isCartPage ? 'main-cart' : 'mini-cart']).then(function (latest) {
            root.dispatchEvent(new CustomEvent('line-item-quantity:change:end', { bubbles: true, detail: { cart: latest || cart } }));
            if (latest) {
              root.dispatchEvent(new CustomEvent('cart:refresh', {
                bubbles: true,
                detail: { cart: latest, openMiniCart: false, replacementDelay: 0 }
              }));
            }
          });
        });
      })
      .catch(function () {});
  }

  if (script && script.hasAttribute('data-check-on-load')) fixOnLoad();
})();
